import {
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps,
} from "@mui/material";
import {
  SmartAutoComplete,
  SmartInputBaseProps,
} from "@nodeon/react-components";
import React from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MapAreaTypeEnum } from "~/api/GeneratedApi";
import { useMapAreas } from "~/utils/hooks/useMapAreas";

type ExtraProps = {
  useProjectsOnly?: boolean;
};

type Props<T extends FieldValues> = Omit<
  Parameters<typeof SmartAutoComplete>[0],
  "options" | "getOptionLabel"
> &
  SmartInputBaseProps<T> &
  ExtraProps;

export const MapAreaSmartAutocomplete = <
  T extends FieldValues,
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = undefined,
>({
  multiple,
  disabled,
  disableClearable,
  freeSolo,
  name,
  label,
  useProjectsOnly = false,
  ...props
}: Props<T>): JSX.Element => {
  const { t } = useTranslation();
  const { mapAreas, mapAreasLoading } = useMapAreas();

  const getOptionLabel = React.useCallback(
    (opt: number | AutocompleteFreeSoloValueMapping<TFreeSolo>) => {
      if (!(typeof opt === "number")) return "";
      const ma = mapAreas.find((x) => x.mapAreaId === opt);
      if (!ma) return t("general:unknown");
      return ma.mapAreaType === MapAreaTypeEnum.Project
        ? `${ma.projectID ? `${ma.projectID} -` : ""} ${ma.name}`
        : ma.name;
    },
    [mapAreas, t],
  );

  const getOptionDisabled = React.useCallback(
    (opt: number | AutocompleteFreeSoloValueMapping<TFreeSolo>) => {
      if (!useProjectsOnly) return false;
      if (!(typeof opt === "number")) return false;
      const ma = mapAreas.find((x) => x.mapAreaId === opt);
      if (!ma) return false;
      return ma.link !== null;
    },
    [useProjectsOnly, mapAreas],
  );

  const options = React.useMemo(() => {
    if (useProjectsOnly)
      return mapAreas
        .filter((a) => a.mapAreaType === MapAreaTypeEnum.Project)
        .map((m) => m.mapAreaId);
    return mapAreas.map((m) => m.mapAreaId);
  }, [useProjectsOnly, mapAreas]);

  return (
    <SmartAutoComplete<T, number, TMultiple, TDisableClearable, TFreeSolo>
      {...(props as Omit<
        AutocompleteProps<number, TMultiple, TDisableClearable, TFreeSolo>,
        "options"
      >)}
      name={name}
      label={label}
      multiple={multiple as TMultiple}
      disableClearable={disableClearable as TDisableClearable}
      freeSolo={freeSolo as TFreeSolo}
      options={options}
      disabled={disabled || mapAreasLoading}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
    />
  );
};
